// VOYA9 — Coming Soon detail page template.
//
// Renders the editorial "why-the-wait" content for in-development
// programs (Cognitive Performance, Longevity & Cellular Health,
// Vitality & Performance), with an inline email capture for early
// access. One template, content-keyed by program slug. Brand-voice
// hardcoded per the spec doc (admin-controlled content is a future
// enhancement).
import { useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { Eyebrow, Headline } from "@/components/Editorial";
import Button from "@/components/Button";
import { apiPost } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "@/components/Toast";
import { C, colorSchemeForProgram } from "@/lib/brand";

// ---- per-program content. Hardcoded for now — moves to admin/CMS
// when the BE adds program-content blocks for in-development tiles.
const PROGRAMS = {
  "cognitive-performance": {
    name: "Cognitive Performance",
    headlineBefore: "COGNITIVE",
    headlineItalic: "performance.",
    subtagline: "Sharper focus. Steadier mind.",
    intro:
      "Cognitive performance is the next frontier of physician-supervised peptide therapy. We're building a program that supports focus, memory, mood regulation, and neural protection — using compounds with established mechanisms alongside emerging investigational nootropics.",
    inclusions: [
      {
        eyebrow: "ESTABLISHED NOOTROPICS",
        body: "Semax and Selank — Russian-developed neuropeptides with decades of clinical use for focus, anxiety regulation, and recovery from cognitive fatigue.",
      },
      {
        eyebrow: "INVESTIGATIONAL NEUROPROTECTIVES",
        body: "DSIP for deep sleep architecture, Cerebrolysin and P21 for neurogenesis support, ARA-290 and VIP for neuroinflammation — these require careful protocols and physician monitoring.",
      },
    ],
    whyCarefully: [
      "Cognitive issues are uniquely complex — anxiety, sleep, hormones, nutrition, and trauma all overlap. Single-compound protocols rarely solve them in isolation.",
      "Investigational neuropeptides have promising data but limited large-trial validation. We require physician monitoring on every protocol, with member-side education before starting.",
      "We're building a member-education content library so people understand what each compound does and what it can't do — before we ask them to commit.",
    ],
    accessBullets: [
      "Priority placement when the program opens",
      "Monthly updates on protocol development progress",
      "Educational content on cognitive peptides during the wait",
      "First-cohort pricing on initial protocols",
    ],
    whileYouWait:
      "Many cognitive complaints have foundational drivers that our existing programs address: weight regulation impacts focus, recovery sleep impacts memory, hormone health impacts mood. Start with an assessment-led consultation and your physician will tell you whether the path to cognitive clarity runs through a program we already offer.",
  },
  "longevity-cellular-health": {
    name: "Longevity & Cellular Health",
    headlineBefore: "LONGEVITY &",
    headlineItalic: "cellular health.",
    subtagline: "The long game.",
    intro:
      "Longevity is the most-asked-about program category — and the most complex to do honestly. We're building a protocol library that focuses on mitochondrial energy, cellular repair, oxidative stress, and immune resilience, using compounds with the strongest evidence and physician oversight on every step.",
    inclusions: [
      {
        eyebrow: "MITOCHONDRIAL SUPPORT",
        body: "MOTS-c, SS-31, Humanin — peptides from your own mitochondrial DNA that support cellular energy production and stress resilience.",
      },
      {
        eyebrow: "CELLULAR AGING SUPPORT",
        body: "NAD+ precursors, Epithalon for telomere support, Thymosin Alpha-1 for immune calibration, Glutathione for oxidative defense.",
      },
    ],
    whyCarefully: [
      "Longevity protocols are long-term commitments — we won't launch one until we're confident in the physician monitoring framework and biomarker tracking.",
      "Combination protocols (which most evidence supports) require careful titration. We're building each compound's profile before stacking them.",
      "Evidence quality varies enormously across longevity peptides. We're explicit about what's well-studied vs. what's promising-but-early.",
    ],
    accessBullets: [
      "Priority placement when the program opens",
      "Monthly updates on protocol development",
      "First-cohort pricing on initial protocols",
      "Quarterly biomarker tracking included for first cohort",
    ],
    whileYouWait:
      "The Recovery & Healing program (BPC-157, Recovery Blend, GHK-Cu) is the closest cousin we offer today — many longevity-curious members benefit from foundational tissue repair while they wait. Start there if it fits.",
  },
  "vitality-performance": {
    name: "Vitality & Performance",
    headlineBefore: "VITALITY &",
    headlineItalic: "performance.",
    subtagline: "Energy, recovery, how you show up.",
    intro:
      "Vitality & Performance is a focused launch — we're starting with growth-hormone-support peptides because they have the strongest evidence and the cleanest safety profile. Other vitality categories (testosterone, thyroid, adrenal) will follow once we have the right physician infrastructure.",
    inclusions: [
      {
        eyebrow: "GROWTH HORMONE SUPPORT",
        body: "CJC-1295 + Ipamorelin (blended weekly) supports natural growth-hormone production for body composition, deeper sleep, recovery, and steady energy. Tesamorelin is reserved for members with specific clinical indications.",
      },
    ],
    whyFocused: [
      "Vitality means different things to different people. Rather than launching broad and shallow, we're starting with the protocol with the cleanest evidence and clearest member outcomes.",
      "Growth-hormone support is our entry point. Testosterone, thyroid, and adrenal-support protocols will follow as we extend the physician network.",
      "Our clinical positioning: we support your body's natural production rather than replacing it. Different from TRT clinics — same monitoring discipline.",
    ],
    experience: [
      { when: "WEEKS 1–3", what: "Sleep depth improves. Recovery times shorten. Subtle energy lift." },
      { when: "MONTH 2–3", what: "Body composition shifts — leaner, stronger feel. Workouts compound faster." },
      { when: "MONTH 4–6", what: "Steady performance gains plateau into your new baseline." },
    ],
    accessBullets: [
      "Priority placement when the program opens (Q3 2026)",
      "Monthly updates on protocol details and physician network",
      "First-cohort pricing on the launch protocol",
    ],
    whileYouWait:
      "Recovery & Healing covers tissue repair and foundational recovery today. Members focused on body composition often benefit from Weight Management as a complementary path — your assessment-led consultation will tell you which fits.",
  },
};

export default function ComingSoonProgram() {
  const { slug } = useParams();
  const content = PROGRAMS[slug];
  if (!content) {
    return <Navigate to="/app/browse" replace />;
  }
  const scheme = colorSchemeForProgram({ slug });
  return (
    <div data-testid={`coming-soon-${slug}`} style={{ background: C.cream, minHeight: "100vh", paddingBottom: 80 }}>
      {/* Header */}
      <section
        style={{
          background: C.ink,
          color: C.cream,
          padding: "64px 24px 96px",
          clipPath: "polygon(0 0, 100% 0, 100% calc(100% - 32px), 0 100%)",
        }}
      >
        <div style={{ maxWidth: 900, margin: "0 auto" }}>
          <Eyebrow color={scheme?.accent || C.peach}>★ COMING SOON</Eyebrow>
          <Headline
            before={content.headlineBefore}
            italic={content.headlineItalic}
            color={C.cream}
            italicColor={scheme?.accent || C.peach}
            size={68}
            className="mt-4"
          />
          <p style={{ marginTop: 18, fontSize: 18, lineHeight: 1.5, color: "rgba(250,246,240,0.78)", fontFamily: '"Instrument Serif", Georgia, serif', fontStyle: "italic" }}>
            {content.subtagline}
          </p>
        </div>
      </section>

      {/* Intro */}
      <section style={{ padding: "32px 24px", background: C.cream }}>
        <div style={{ maxWidth: 720, margin: "0 auto" }}>
          <p style={{ fontSize: 16, lineHeight: 1.7, color: C.ink, margin: 0 }}>
            {content.intro}
          </p>
        </div>
      </section>

      {/* What the program will include */}
      <section style={{ padding: "24px 24px 8px" }}>
        <div style={{ maxWidth: 720, margin: "0 auto" }}>
          <Eyebrow color={scheme?.accent || C.peachDeep}>★ WHAT THE PROGRAM WILL INCLUDE</Eyebrow>
          <div style={{ marginTop: 18, display: "flex", flexDirection: "column", gap: 18 }}>
            {content.inclusions.map((s) => (
              <div key={s.eyebrow} style={{ padding: "16px 18px", background: C.bone, borderLeft: `3px solid ${scheme?.accent || C.peachDeep}` }}>
                <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.18em", color: scheme?.accent || C.peachDeep }}>
                  {s.eyebrow}
                </div>
                <p style={{ marginTop: 8, fontSize: 14, color: C.ink, lineHeight: 1.65, margin: "8px 0 0" }}>{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Member-experience timeline (Vitality only) */}
      {content.experience && (
        <section style={{ padding: "24px 24px 8px" }}>
          <div style={{ maxWidth: 720, margin: "0 auto" }}>
            <Eyebrow color={scheme?.accent || C.peachDeep}>★ WHAT MEMBERS WILL EXPERIENCE</Eyebrow>
            <div style={{ marginTop: 18, display: "grid", gridTemplateColumns: "1fr", gap: 14 }} className="!grid-cols-1 md:!grid-cols-3">
              {content.experience.map((e) => (
                <div key={e.when} style={{ padding: "16px 18px", background: C.bone }}>
                  <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.22em", color: C.warmGray }}>{e.when}</div>
                  <p style={{ marginTop: 6, fontSize: 14, lineHeight: 1.55, color: C.ink, margin: "6px 0 0" }}>{e.what}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Why we're building this carefully (or launching focused) */}
      <section style={{ padding: "24px 24px 8px" }}>
        <div style={{ maxWidth: 720, margin: "0 auto" }}>
          <Eyebrow color={scheme?.accent || C.peachDeep}>
            ★ {content.whyFocused ? "WHY WE'RE LAUNCHING FOCUSED" : "WHY WE'RE BUILDING THIS CAREFULLY"}
          </Eyebrow>
          <ul style={{ marginTop: 16, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 12 }}>
            {(content.whyFocused || content.whyCarefully).map((r, i) => (
              <li
                key={i}
                style={{
                  paddingLeft: 20,
                  position: "relative",
                  fontSize: 14,
                  lineHeight: 1.65,
                  color: C.ink,
                }}
              >
                <span style={{ position: "absolute", left: 0, color: scheme?.accent || C.peachDeep, fontWeight: 800 }}>•</span>
                {r}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Early access — email capture */}
      <section style={{ padding: "32px 24px 8px" }}>
        <div style={{ maxWidth: 720, margin: "0 auto", padding: "28px 28px 24px", background: C.ink, color: C.cream }}>
          <Eyebrow color={C.peach}>★ EARLY ACCESS</Eyebrow>
          <Headline before="NOTIFY ME WHEN" italic={`${content.name.toLowerCase()} launches.`} color={C.cream} italicColor={C.peach} size={36} className="mt-3" />
          <ul style={{ marginTop: 18, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 8, color: "rgba(250,246,240,0.78)" }}>
            {content.accessBullets.map((b) => (
              <li key={b} style={{ paddingLeft: 18, position: "relative", fontSize: 14, lineHeight: 1.55 }}>
                <span style={{ position: "absolute", left: 0, color: C.peach, fontWeight: 800 }}>•</span>
                {b}
              </li>
            ))}
          </ul>
          <WaitlistForm programSlug={slug} programName={content.name} />
        </div>
      </section>

      {/* While you wait */}
      <section style={{ padding: "32px 24px 80px" }}>
        <div style={{ maxWidth: 720, margin: "0 auto" }}>
          <Eyebrow color={C.warmGray}>★ WHILE YOU WAIT</Eyebrow>
          <p style={{ marginTop: 14, fontSize: 15, lineHeight: 1.7, color: C.ink, margin: "14px 0 0" }}>
            {content.whileYouWait}
          </p>
          <div style={{ marginTop: 22, display: "flex", gap: 12, flexWrap: "wrap" }}>
            <Link to="/app/browse"><Button variant="primary" size="md" testId="coming-soon-back-to-browse">← BROWSE OTHER PROGRAMS</Button></Link>
            <Link to="/app/home"><Button variant="ghost" size="md" testId="coming-soon-go-home">START AN ASSESSMENT</Button></Link>
          </div>
        </div>
      </section>
    </div>
  );
}

// Waitlist form — POSTs to the program-waitlist endpoint when authenticated
// or anonymously with email. Falls back gracefully if the BE hasn't shipped.
function WaitlistForm({ programSlug, programName }) {
  const { consumer } = useAuth();
  const [email, setEmail] = useState(consumer?.email || "");
  const [name, setName] = useState(consumer ? `${consumer.firstName || ""} ${consumer.lastName || ""}`.trim() : "");
  const [interest, setInterest] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const value = email.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      toast("Enter a valid email.", "error");
      return;
    }
    setBusy(true);
    try {
      await apiPost("/consumer/program-waitlist", {
        programSlug,
        email: value,
        name: name.trim() || undefined,
        interest: interest.trim() || undefined,
      }, { auth: false });
      setDone(true);
      toast(`You're on the ${programName} waitlist.`, "success");
    } catch (err) {
      if (err?.status === 404) {
        toast("Waitlist isn't enabled yet — check back soon.", "error");
      } else {
        toast(err?.detail || "Couldn't sign you up. Try again.", "error");
      }
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div data-testid="waitlist-success" style={{ marginTop: 24, padding: "18px 18px", background: "rgba(250,246,240,0.08)", borderLeft: `3px solid ${C.peach}` }}>
        <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.22em", color: C.peach }}>
          ★ YOU'RE ON THE LIST
        </div>
        <p style={{ marginTop: 8, fontSize: 14, color: "rgba(250,246,240,0.82)", lineHeight: 1.55, margin: "8px 0 0" }}>
          We'll email you the moment {programName} opens — and send monthly updates on protocol development in the meantime.
        </p>
      </div>
    );
  }

  return (
    <form data-testid="waitlist-form" onSubmit={submit} style={{ marginTop: 24, display: "flex", flexDirection: "column", gap: 12 }}>
      <WaitlistField label="EMAIL">
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@email.com"
          data-testid="waitlist-email"
          required
          style={waitlistInputStyle}
        />
      </WaitlistField>
      <WaitlistField label="NAME (OPTIONAL)">
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="First Last"
          data-testid="waitlist-name"
          style={waitlistInputStyle}
        />
      </WaitlistField>
      <WaitlistField label="WHAT INTERESTS YOU? (OPTIONAL)">
        <textarea
          value={interest}
          onChange={(e) => setInterest(e.target.value)}
          rows={3}
          placeholder="Sleep quality, focus, recovery, body composition…"
          data-testid="waitlist-interest"
          style={{ ...waitlistInputStyle, resize: "vertical", minHeight: 64 }}
        />
      </WaitlistField>
      <div style={{ marginTop: 6 }}>
        <Button type="submit" variant="peach" size="lg" block testId="waitlist-submit" disabled={busy}>
          {busy ? "ADDING…" : "ADD ME TO THE LIST →"}
        </Button>
      </div>
    </form>
  );
}

function WaitlistField({ label, children }) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.22em", color: "rgba(250,246,240,0.55)" }}>{label}</span>
      <div style={{ padding: "10px 12px", background: "rgba(250,246,240,0.06)", border: "1px solid rgba(250,246,240,0.14)" }}>
        {children}
      </div>
    </label>
  );
}

const waitlistInputStyle = {
  width: "100%",
  border: 0,
  outline: "none",
  background: "transparent",
  fontFamily: '"DM Sans", sans-serif',
  fontSize: 16, // 16+ avoids iOS auto-zoom
  color: C.cream,
  padding: 0,
};
